import type { AiSettings } from '@/types';

function buildDefaultSchedule() {
	return ([0, 1, 2, 3, 4, 5, 6] as const).map((day) => ({
		day,
		enabled: false,
		allDay: false,
		slots: [] as { startTime: string; endTime: string }[]
	}));
}

/**
 * Shared helper — returns a complete AiSettings object with sane defaults for every
 * field. Used by every settings page that calls useUpdateAiSettings so that a PUT
 * always sends the full payload and never accidentally zeros out fields owned by
 * another page.
 */
export function buildDefaultAiSettings(existing: AiSettings | null | undefined): AiSettings {
	return {
		// No browser-timezone fallback here — the store's timezone is the source of truth.
		// An account with nothing saved yet gets an empty timezone; the Operating Hours page
		// pre-fills it from Shopify (or prompts the operator to pick one) before it is ever saved.
		operatingHours: existing?.operatingHours ?? {
			mode: 'custom',
			timezone: '',
			schedule: buildDefaultSchedule()
		},
		businessHours: existing?.businessHours ?? { days: [], startTime: '', endTime: '' },
		overflow: existing?.overflow ?? {
			email: { enabled: false, thresholdMinutes: 10 },
			livechat: { enabled: false, thresholdMinutes: 5 },
			sms: { enabled: false, thresholdMinutes: 10 }
		},
		tagName: existing?.tagName ?? 'managed-by-kim',
		...(existing?.automationTag && { automationTag: existing.automationTag }),
		processTags: existing?.processTags ?? [],
		ignoreTags: existing?.ignoreTags ?? [],
		aiPlan: existing?.aiPlan ?? { trialEnabled: false },
		confidenceThreshold: existing?.confidenceThreshold ?? 0.5,
		skipHumanAssigned: existing?.skipHumanAssigned ?? false,
		skipHumanAssignedExceptEmails: existing?.skipHumanAssignedExceptEmails ?? []
	};
}

/** Whether a brand has both a timezone and complete business hours — what business-hours features need. */
export function hasBrandBusinessTime(settings: AiSettings | null | undefined): boolean {
	const bh = settings?.businessHours;
	return Boolean(
		settings?.operatingHours?.timezone &&
			bh?.days?.length &&
			bh.startTime &&
			bh.endTime &&
			bh.startTime < bh.endTime,
	);
}
